import { useEffect, useRef, useState } from "react";
import { MdSearch } from "react-icons/md";

import { Box, Input, InputGroup, Stack } from "@chakra-ui/react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { rem } from "polished";

import { CheckboxOption } from "./checkbox-option";
import { CheckboxOptionType } from "./types";
import { normalizeString } from "./utils";

type CheckboxSelectListProps<T extends string> = {
  name: string;
  values: T[];
  onChangeValues?: (values: T[]) => void;
  options: CheckboxOptionType<T>[];
  focusOnMount?: boolean;
};

export const CheckboxSelectList = <T extends string>({
  name,
  options,
  values,
  onChangeValues,
  focusOnMount = false,
}: CheckboxSelectListProps<T>) => {
  const [search, setSearch] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current && focusOnMount) inputRef.current.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedOptions = options.filter(option => values.includes(option.value));

  // Then filter over the sorted elements (ignoring accents)
  const filteredSelectedOptions = selectedOptions.filter(option =>
    normalizeString(option.label).includes(normalizeString(search))
  );

  const filteredOptions = options.filter(option =>
    normalizeString(option.label).includes(normalizeString(search))
  );

  const sortedFilteredOptions = filteredOptions.sort((a, b) => a.label.localeCompare(b.label));

  const handleChange = (option: CheckboxOptionType<T>, value: boolean) => {
    onChangeValues?.(value ? [...values, option.value] : values.filter(v => v !== option.value));
  };

  // Virtualizer for sortedFilteredOptions
  const parentRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: sortedFilteredOptions.length,
    getScrollElement: () => parentRef.current,
    estimateSize: index => {
      const option = sortedFilteredOptions[index];
      return option.description ? 55 : 35; // Adjust height based on description presence
    },
    overscan: 10,
  });

  return (
    <Box paddingBlock="6px" paddingInline={0}>
      <InputGroup marginInline="6px" paddingLeft={1} startElement={<MdSearch size={rem(20)} />}>
        <Input
          borderColor="transparent"
          _focus={{ outline: "none" }}
          value={search}
          onChange={e => setSearch(e.target.value)}
          ref={inputRef}
          size="md"
          placeholder="Rechercher..."
        />
      </InputGroup>
      {/* selected options part */}
      {filteredSelectedOptions.length > 0 && (
        <>
          <Stack
            maxHeight="200px"
            overflowY="auto"
            paddingInline="6px"
            py={1}
            direction="column"
            gap={0}
          >
            {filteredSelectedOptions.map(option => (
              <CheckboxOption
                key={option.value}
                label={option.label}
                description={option.description}
                value={`${option.value}_selected`}
                name={name}
                onChangeValue={value => handleChange(option, value)}
                checked={values.includes(option.value)}
              />
            ))}
          </Stack>
          <Box h="2px" backgroundColor="gray.200" />
        </>
      )}
      {/*  all options part - virtualized */}
      <Box
        ref={parentRef}
        maxHeight="300px"
        overflow="auto"
        position="relative"
        paddingInline="6px"
        py={1}
      >
        <Box height={virtualizer.getTotalSize()} position="relative">
          {virtualizer.getVirtualItems().map(virtualRow => {
            const option = filteredOptions[virtualRow.index];
            return (
              <Box
                key={option.value}
                position="absolute"
                top={0}
                left={0}
                width="100%"
                style={{
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              >
                <CheckboxOption
                  label={option.label}
                  description={option.description}
                  value={option.value}
                  name={name}
                  onChangeValue={value => handleChange(option, value)}
                  checked={values.includes(option.value)}
                  disabled={option.disabled || values.includes(option.value)}
                />
              </Box>
            );
          })}
        </Box>
        {filteredOptions.length === 0 && (
          <Box paddingInline="6px" py={1} textAlign="center" color="gray.500">
            Aucun résultat de recherche...
          </Box>
        )}
      </Box>
    </Box>
  );
};
